import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import toast from 'react-hot-toast';
import useSWR from 'swr';
import { createId } from '@paralleldrive/cuid2';
import { CalendarBlankIcon, CalendarDotsIcon, ArrowsDownUpIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useWebsocket } from '../../../contexts/websocket-context';

import { PageHeader } from '../../../components/PageHeader';
import { PageHeading } from '../../../components/PageHeading';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { SpinnerBlock } from '../../../components/Spinner';
import { Input } from '../../../components/input/Input';
import { Button } from '../../../components/button/Button';
import { EmptyState } from '../../../components/empty-state/EmptyState';
import classNames from '../../../utils/classnames';
import { formatDate, formatInputDate } from '../../../utils/date';
import { useSearchParam } from '../../../hooks/useSearchParam';
import { getDisplayError } from '../../../utils/get-display-error';
import { trackEvent } from '../../../utils/track-event';
import { truckMatchesSearch } from '../utils';
import { ErrorState } from '../../../components/error-state/ErrorState';
import { ToggleGroup, type ToggleGroupOption } from '../../../components/toggle-group/ToggleGroup';

import {
  getPlanningBoardTrucks,
  getPlanningBoardBacklog,
  assignPlanningBoardOrder,
  reorderPlanningBoardTrucks,
  PlanningBoardTruck,
  PlanningBoardTruckOrder,
  PlanningBoardBacklogOrder,
  Region,
} from '../models';
import { canTrailerCarryLoad } from '../endpoints/shared.types';

import { TruckCard } from './components/TruckCard';
import { OrderBacklog } from './components/OrderBacklog';
import { PlannerSelect } from './components/PlannerSelect';
import { WeeklyPlanningBoard } from './weekly/WeeklyPlanningBoard';

// ============================================================================
// Types
// ============================================================================

export interface DragItem {
  type: 'order';
  order: PlanningBoardBacklogOrder | PlanningBoardTruckOrder;
  sourceTruckId: string | null;
}

interface TruckDragItem {
  type: 'truck';
  truckId: string;
}

// ============================================================================
// TruckColumnWithDnd — wraps TruckCard with drag (header) + drop (column)
// ============================================================================

interface TruckColumnWithDndProps {
  truck: PlanningBoardTruck;
  date: string;
  onOrderDrop: (order: PlanningBoardBacklogOrder | PlanningBoardTruckOrder, sourceTruckId: string | null) => void;
  onRefresh: () => void;
  onTruckDrop: (draggedId: string, targetId: string) => void;
}

const TruckColumnWithDnd: React.FC<TruckColumnWithDndProps> = ({ truck, onTruckDrop, ...truckCardProps }) => {
  const [{ isDragging }, drag] = useDrag<TruckDragItem, void, { isDragging: boolean }>(
    () => ({
      type: 'truck',
      item: { type: 'truck', truckId: truck.id },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [truck.id],
  );

  const [{ isOver }, drop] = useDrop<TruckDragItem, void, { isOver: boolean }>(
    () => ({
      accept: 'truck',
      drop: (item) => {
        if (item.truckId !== truck.id) {
          onTruckDrop(item.truckId, truck.id);
        }
      },
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [truck.id, onTruckDrop],
  );

  return (
    <div
      ref={(node) => {
        drop(node);
      }}
      className={classNames('transition-opacity', isDragging && 'opacity-40', isOver && 'ring-2 ring-brand rounded-lg')}
    >
      <TruckCard truck={truck} headerDragRef={drag} {...truckCardProps} />
    </div>
  );
};

// ============================================================================
// Page — daily or weekly view
// ============================================================================

type PlanningBoardView = 'day' | 'week';

const VIEW_OPTIONS: ToggleGroupOption<PlanningBoardView>[] = [
  { value: 'day', label: 'Dagelijks', icon: <CalendarBlankIcon /> },
  { value: 'week', label: 'Wekelijks', icon: <CalendarDotsIcon /> },
];

export const PlanningBoardPage: React.FC = () => {
  const [view, setView] = useSearchParam<PlanningBoardView>('view', 'day');
  const viewToggle = <ToggleGroup options={VIEW_OPTIONS} value={view} onChange={setView} />;

  return view === 'week' ? (
    <WeeklyPlanningBoard viewToggle={viewToggle} />
  ) : (
    <DailyPlanningBoard viewToggle={viewToggle} />
  );
};

// ============================================================================
// Daily board
// ============================================================================

const DailyPlanningBoard: React.FC<{ viewToggle: React.ReactNode }> = ({ viewToggle }) => {
  const { websocket: wsClient } = useWebsocket();

  // State
  const [urlDate, setUrlDate] = useSearchParam('date', formatInputDate(new Date()));
  // Local state controls the Input directly (like before); the search param is only
  // used to seed the initial value, and gets written back on every edit.
  const [date, setLocalDate] = useState(urlDate);
  const setDate = (newDate: string) => {
    setLocalDate(newDate);
    setUrlDate(newDate);
  };
  const [plannerIds, setPlannerIds] = useState<string[]>([]);
  const [dateType, setDateType] = useState<'load' | 'unload'>('load');
  const [region, setRegion] = useState<Region>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortAlpha, setSortAlpha] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');
  const [localTruckOrder, setLocalTruckOrder] = useState<string[]>([]);

  // Debounce search query to avoid API call on every keystroke
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Fetch trucks data
  const {
    data: trucksData,
    error: trucksError,
    isLoading: trucksLoading,
    mutate: mutateTrucks,
  } = useSWR(['planning-board-trucks', date], () => getPlanningBoardTrucks(date), {
    revalidateOnFocus: false,
    // Belt-and-suspenders backstop for a missed websocket message: only poll while a
    // calculation is plausibly still pending (a non-frozen order with no autoEmptyKm yet).
    refreshInterval: (latestData) => {
      const hasPendingAutoEmptyKm = (latestData?.trucks ?? []).some((truck) =>
        truck.orders.some((order) => {
          const isFrozen = order.manualFullKm != null || order.manualEmptyKm != null || order.status === 'Executed';
          return !isFrozen && order.autoEmptyKm == null;
        }),
      );
      return hasPendingAutoEmptyKm ? 8000 : 0;
    },
  });

  // Fetch backlog data (uses debounced search to avoid excessive requests)
  const {
    data: backlogData,
    error: backlogError,
    isLoading: backlogLoading,
    mutate: mutateBacklog,
  } = useSWR(
    ['planning-board-backlog', date, dateType, region, debouncedSearch],
    () =>
      getPlanningBoardBacklog({
        date,
        dateType,
        region,
        search: debouncedSearch || undefined,
      }),
    { revalidateOnFocus: false },
  );

  // Subscribe to real-time auto-empty-km recalculations for the currently viewed date
  useEffect(() => {
    wsClient.send({
      ref: createId(),
      method: 'planning-board/subscribe',
      data: { date },
    });

    const disposable = wsClient.onMessage((msg) => {
      if (msg.method === 'planning-board/orders-updated' && msg.data.date === date) {
        mutateTrucks();
      }
    });

    return () => disposable.dispose();
  }, [date]);

  // Find truck by ID for trailer type validation
  const trucksMap = useMemo(() => {
    const map = new Map<string, PlanningBoardTruck>();
    for (const truck of trucksData?.trucks ?? []) {
      map.set(truck.id, truck);
    }
    return map;
  }, [trucksData?.trucks]);

  // Sync localTruckOrder when trucks are added/removed; preserve manual order for existing trucks
  useEffect(() => {
    if (!trucksData?.trucks) return;
    const incoming = trucksData.trucks.map((t) => t.id);
    const incomingSet = new Set(incoming);
    setLocalTruckOrder((prev) => {
      const preserved = prev.filter((id) => incomingSet.has(id));
      const newIds = incoming.filter((id) => !prev.includes(id));
      if (preserved.length === prev.length && newIds.length === 0 && preserved.length === incoming.length) {
        return prev;
      }
      return [...preserved, ...newIds];
    });
  }, [trucksData?.trucks]);

  // Trucks of the selected planners (no selection = every planner)
  const plannerTrucks = useMemo(() => {
    const trucks = trucksData?.trucks ?? [];
    if (plannerIds.length === 0) return trucks;
    return trucks.filter((t) => t.planner != null && plannerIds.includes(t.planner.id));
  }, [trucksData?.trucks, plannerIds]);

  // Displayed trucks: custom (persisted) order unless A-Z is active
  const displayedTrucks = useMemo(() => {
    const trucks = plannerTrucks;
    if (sortAlpha) return [...trucks].sort((a, b) => (a.licensePlate ?? '').localeCompare(b.licensePlate ?? ''));
    if (localTruckOrder.length === 0) return trucks;
    const map = new Map(trucks.map((t) => [t.id, t]));
    return localTruckOrder.map((id) => map.get(id)).filter(Boolean) as PlanningBoardTruck[];
  }, [plannerTrucks, sortAlpha, localTruckOrder]);

  // Apply column search filter on top of the current order/sorting
  const filteredTrucks = useMemo(
    () => displayedTrucks.filter((truck) => truckMatchesSearch(truck, columnSearch)),
    [displayedTrucks, columnSearch],
  );

  // Debounced refresh to handle rapid consecutive order drops.
  // SWR's default dedup interval (2s) can cause a revalidation triggered by
  // a second drop to reuse the result from the first — which was fetched before
  // the second assignment was committed. Debouncing ensures a single fresh GET
  // fires only after all concurrent drops have settled.
  const dropRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (dropRefreshTimer.current) clearTimeout(dropRefreshTimer.current);
    },
    [],
  );

  // Handle order drop on truck
  const handleOrderDrop = useCallback(
    async (
      order: PlanningBoardBacklogOrder | PlanningBoardTruckOrder,
      targetTruckId: string | null,
      sourceTruckId: string | null,
    ) => {
      // Validate trailer type compatibility when assigning to a truck
      if (targetTruckId) {
        const targetTruck = trucksMap.get(targetTruckId);
        if (targetTruck && !canTrailerCarryLoad(targetTruck.defaultTrailerType, order.allowedTrailerTypes)) {
          toast.error(
            `Incompatibel trailertype: ${order.allowedTrailerTypes.join(', ')} past niet op ${targetTruck.defaultTrailerType ?? 'onbekend'} trailer`,
          );
          return;
        }
      }

      try {
        await assignPlanningBoardOrder({
          orderId: order.id,
          supplierTruckId: targetTruckId,
          date,
        });

        trackEvent('planning_board_order_assigned', {
          order_id: order.id,
          target_truck_id: targetTruckId,
          source_truck_id: sourceTruckId,
          unassigned: targetTruckId === null,
        });

        // Debounce refresh so rapid consecutive drops produce one fresh fetch
        // after the last assignment is committed.
        if (dropRefreshTimer.current) clearTimeout(dropRefreshTimer.current);
        dropRefreshTimer.current = setTimeout(() => {
          mutateTrucks();
          mutateBacklog();
        }, 100);

        if (targetTruckId) {
          toast.success('Order toegewezen aan truck');
        } else {
          toast.success('Order verwijderd van truck');
        }
      } catch (err) {
        toast.error(`Kon order niet toewijzen: ${getDisplayError(err)}`);
      }
    },
    [date, mutateTrucks, mutateBacklog, trucksMap],
  );

  // Memoize refresh callback to prevent TruckCard useDrop re-registration
  const handleRefresh = useCallback(() => {
    mutateTrucks();
    mutateBacklog();
  }, [mutateTrucks, mutateBacklog]);

  // Persist truck column order when a truck is dragged to a new position
  const handleTruckReorder = useCallback(
    async (draggedId: string, targetId: string) => {
      const current = localTruckOrder.length > 0 ? localTruckOrder : (trucksData?.trucks ?? []).map((t) => t.id);
      const fromIdx = current.indexOf(draggedId);
      const toIdx = current.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

      const newOrder = [...current];
      newOrder.splice(fromIdx, 1);
      const insertIdx = fromIdx < toIdx ? toIdx - 1 : toIdx;
      newOrder.splice(insertIdx, 0, draggedId);
      setLocalTruckOrder(newOrder);
      setSortAlpha(false);

      try {
        await reorderPlanningBoardTrucks({
          trucks: newOrder.map((id, idx) => ({ id, sortIdx: idx })),
        });
      } catch (err) {
        toast.error(`Failed to reorder trucks: ${getDisplayError(err)}`);
        setLocalTruckOrder(current);
      }
    },
    [localTruckOrder, trucksData?.trucks],
  );

  // Calculate planned count
  const plannedCount = plannerTrucks.reduce((sum, t) => sum + t.orders.length, 0);
  // Use counts.all from backlog response for accurate total (orders.length is capped at 100)
  const totalCount = plannedCount + (backlogData?.counts.all ?? 0);

  // Memoize per-truck callbacks to avoid creating new functions every render
  const truckCallbacks = useMemo(() => {
    if (!trucksData?.trucks) return new Map();
    const map = new Map<
      string,
      {
        onOrderDrop: (order: PlanningBoardBacklogOrder | PlanningBoardTruckOrder, sourceTruckId: string | null) => void;
      }
    >();
    for (const truck of filteredTrucks) {
      map.set(truck.id, {
        onOrderDrop: (order, sourceTruckId) => handleOrderDrop(order, truck.id, sourceTruckId),
      });
    }
    return map;
  }, [filteredTrucks, trucksData?.trucks, handleOrderDrop]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-screen flex flex-col -mb-32">
        <PageHeader title="Planning Board" />

        <PageHeading
          noMarginBottom
          leftSide={
            <Breadcrumb currentItem="Planning board" meta={`${plannedCount} van ${totalCount} ladingen gepland`} />
          }
          rightSide={
            <>
              {viewToggle}
              <PlannerSelect planners={trucksData?.planners ?? []} selectedIds={plannerIds} onChange={setPlannerIds} />
              <div className="w-72">
                <Input
                  type="text"
                  value={columnSearch}
                  onChange={setColumnSearch}
                  placeholder="Zoek vervoerder, kenteken…"
                  iconLeft={<MagnifyingGlassIcon className="input-icon" />}
                />
              </div>
              <div className="w-48">
                <Input
                  type="date"
                  value={date}
                  onChange={setDate}
                  iconLeft={<CalendarBlankIcon className="input-icon" />}
                />
              </div>
              <Button
                color={sortAlpha ? 'primary' : 'secondary'}
                width="w-9 px-0"
                ariaLabel="Sorteer voertuigen op kenteken (A–Z)"
                title={
                  sortAlpha ? 'Gesorteerd op kenteken (A–Z) — klik voor eigen volgorde' : 'Sorteer op kenteken (A–Z)'
                }
                onTrigger={() => setSortAlpha((v) => !v)}
                iconLeft={<ArrowsDownUpIcon className="h-4 w-4" weight="bold" />}
              />
            </>
          }
        />

        {/* Main Content */}
        <div className="flex flex-1 gap-5 overflow-hidden p-5">
          {/* Left rail - backlog */}
          <div className="flex w-80 min-h-0 shrink-0 flex-col">
            {backlogError ? (
              <ErrorState error={backlogError} onRetry={() => mutateBacklog()} variant="inline" />
            ) : (
              <OrderBacklog
                orders={backlogData?.orders ?? []}
                counts={backlogData?.counts ?? { all: 0, benelux: 0, france: 0, germany: 0, forwarding: 0 }}
                plannedCount={plannedCount}
                totalCount={totalCount}
                isLoading={backlogLoading}
                dateType={dateType}
                onDateTypeChange={setDateType}
                region={region}
                onRegionChange={setRegion}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onOrderDrop={handleOrderDrop}
              />
            )}
          </div>

          {/* Right - fleet */}
          <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-auto">
            <div className="flex items-baseline gap-2 px-0.5">
              <h2 className="text-sm font-semibold text-fg">Vloot — {formatDate(date)}</h2>
              {trucksData && (
                <span className="caption-text">
                  {filteredTrucks.length === displayedTrucks.length
                    ? `${displayedTrucks.length} ${displayedTrucks.length === 1 ? 'voertuig' : 'voertuigen'}`
                    : `${filteredTrucks.length} van ${displayedTrucks.length} voertuigen`}
                </span>
              )}
            </div>

            {trucksError ? (
              <ErrorState error={trucksError} onRetry={() => mutateTrucks()} variant="section" />
            ) : trucksLoading ? (
              <SpinnerBlock message="Voertuigen laden…" />
            ) : filteredTrucks.length === 0 ? (
              <EmptyState
                variant="panel"
                icon={<MagnifyingGlassIcon />}
                title={
                  columnSearch.trim()
                    ? `Geen vervoerders gevonden voor "${columnSearch.trim()}"`
                    : 'Geen vervoerders gevonden'
                }
                description={columnSearch.trim() ? 'Pas je zoekopdracht aan of wis het zoekveld.' : undefined}
              />
            ) : (
              <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2 2xl:grid-cols-3">
                {filteredTrucks.map((truck) => {
                  const callbacks = truckCallbacks.get(truck.id);
                  return (
                    <TruckColumnWithDnd
                      key={truck.id}
                      truck={truck}
                      date={date}
                      onOrderDrop={callbacks!.onOrderDrop}
                      onRefresh={handleRefresh}
                      onTruckDrop={handleTruckReorder}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </DndProvider>
  );
};
